export const ATTRIBUTION_TTL_MS = 1_209_600_000
export const MAX_ATTRIBUTION_ITEMS = 50

export const ATTRIBUTION_PRODUCT_TYPE_VALUES = [
  'marketplace',
  'own-brand',
  'sae',
] as const

export type AttributionProductType =
  (typeof ATTRIBUTION_PRODUCT_TYPE_VALUES)[number]

export type AttributionProductIdentity = {
  offerId?: string
  productId: string
}

export type AttributionClick = AttributionProductIdentity & {
  clickId: string
  createdAt: number
  productType?: AttributionProductType
}

export type AttributionCartItem = AttributionClick & {
  quantity: number
}

export type AttributionAddToCartInput = Partial<AttributionProductIdentity> & {
  productFamilyIds?: string[]
  quantity: number
}

type AttributionMatchInput = Omit<AttributionAddToCartInput, 'quantity'>

export const attributionGetCappedItems = <Item>(items: Item[]): Item[] =>
  items.slice(-MAX_ATTRIBUTION_ITEMS)

export const attributionRemoveExpiredItems = <
  Item extends Pick<AttributionClick, 'createdAt'>,
>(
  items: Item[],
): Item[] => {
  const oldestCreatedAt = Date.now() - ATTRIBUTION_TTL_MS

  return items.filter(({ createdAt }) => createdAt > oldestCreatedAt)
}

export const attributionFindLatestMatchingItem = <
  Item extends AttributionProductIdentity,
>(
  items: Item[],
  { offerId, productFamilyIds = [], productId }: AttributionMatchInput,
): Item | undefined => {
  const latestItems = items.toReversed()
  const matchingOfferItem = offerId
    ? latestItems.find((item) => item.offerId === offerId)
    : undefined
  const matchingProductIds = new Set([
    ...(productId ? [productId] : []),
    ...productFamilyIds,
  ])

  return (
    matchingOfferItem ??
    (matchingProductIds.size > 0
      ? latestItems.find((item) => matchingProductIds.has(item.productId))
      : undefined)
  )
}
